import { useEffect, useState } from "react";
import { supabase, STOCK_VIEW } from "@/lib/supabase";
import { useCurrency } from "@/lib/currency";
import { MapPin, Package, Loader2, AlertTriangle } from "lucide-react";

/**
 * Live stock availability for a product, pulled from the Supabase
 * `website_stock_public` view. Lookup by SKU or barcode.
 *
 * Usage:
 *   <ProductStockAvailability sku="IP15" />
 *   <ProductStockAvailability barcode="5060000000001" />
 *
 * The view only exposes safe fields. No cost prices, suppliers, customers
 * or staff are ever pulled.
 */

const STATUS_STYLES = {
  in_stock: { label: "In stock", className: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200" },
  low_stock: { label: "Low stock", className: "bg-amber-100 text-amber-700 ring-1 ring-amber-200" },
  out_of_stock: { label: "Out of stock", className: "bg-red-100 text-red-700 ring-1 ring-red-200" },
};

function normalizeStatus(raw) {
  const v = String(raw || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (v === "in_stock" || v === "available" || v === "in") return "in_stock";
  if (v === "low_stock" || v === "low") return "low_stock";
  if (v === "out_of_stock" || v === "out" || v === "unavailable" || v === "sold_out") return "out_of_stock";
  return v || "out_of_stock";
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { label: status || "Unknown", className: "bg-slate-100 text-slate-700 ring-1 ring-slate-200" };
  return (
    <span
      data-testid={`stock-status-${status}`}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${s.className}`}
    >
      {s.label}
    </span>
  );
}

export default function ProductStockAvailability({ sku, barcode }) {
  const [state, setState] = useState({ loading: true, rows: [], error: null });
  const { format } = useCurrency();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!supabase) {
        if (!cancelled) setState({ loading: false, rows: [], error: "Live stock service is not configured" });
        return;
      }
      if (!sku && !barcode) {
        if (!cancelled) setState({ loading: false, rows: [], error: null });
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      const column = sku ? "sku" : "barcode";
      const value = sku || barcode;
      const { data, error } = await supabase
        .from(STOCK_VIEW)
        .select("product_id,sku,barcode,name,category,sell_price,image_url,branch_id,branch_label,stock_status,public_quantity,available_quantity,updated_at")
        .eq(column, value);
      if (cancelled) return;
      if (error) {
        setState({ loading: false, rows: [], error: error.message });
        return;
      }
      setState({ loading: false, rows: data || [], error: null });
    }
    load();
    return () => { cancelled = true; };
  }, [sku, barcode]);

  if (!sku && !barcode) return null;

  if (state.loading) {
    return (
      <div className="border border-slate-200 rounded-xl p-5 bg-white flex items-center gap-3 text-sm text-slate-500" data-testid="stock-loading">
        <Loader2 className="animate-spin text-[#1a2d5e]" size={16} />
        Checking live availability…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="border border-amber-200 rounded-xl p-5 bg-amber-50 flex items-start gap-3 text-sm text-amber-800" data-testid="stock-error">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" />
        <div>Live stock unavailable right now. Please call or message us to check.</div>
      </div>
    );
  }

  if (state.rows.length === 0) {
    return (
      <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 text-sm text-slate-500" data-testid="stock-empty">
        Live stock for this item is not published yet. Please contact us to check availability.
      </div>
    );
  }

  const head = state.rows[0];
  const price = Number(head.sell_price);
  const hasPrice = Number.isFinite(price) && price > 0;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="stock-availability">
      <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="overline text-[#1a2d5e]">Live availability</div>
          <div className="font-display font-bold text-lg text-[#0b1838] truncate" data-testid="stock-product-name">{head.name}</div>
          {head.category && <div className="text-xs text-slate-500 mt-0.5">{head.category}</div>}
        </div>
        {hasPrice && (
          <div className="font-display font-black text-2xl text-[#1a2d5e] shrink-0" data-testid="stock-price">{format(price)}</div>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {state.rows.map((row) => {
          const rawStatus = normalizeStatus(row.stock_status);
          // If view exposes available_quantity = 0, force out_of_stock regardless of stock_status
          const status = (row.available_quantity === 0) ? "out_of_stock" : rawStatus;
          const hasQty = row.public_quantity !== null && row.public_quantity !== undefined && row.public_quantity !== "";
          return (
            <li key={`${row.branch_label}-${row.sku}-${row.product_id ?? ""}`} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`stock-row-${(row.branch_label || "branch").toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg shrink-0">
                  <MapPin size={16} className="text-[#1a2d5e]" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#0b1838] truncate">{row.branch_label}</div>
                  {hasQty && (
                    <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                      <Package size={12} /> Qty: <span data-testid={`stock-qty-${(row.branch_label || "branch").toLowerCase().replace(/\s+/g, "-")}`}>{row.public_quantity}</span>
                    </div>
                  )}
                </div>
              </div>
              <StatusBadge status={status} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
